import {
  AddOutlined,
  GradingOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import { usePipeline } from "../../pipelines/PipelineContext";
import EventNoteOutlined from "@mui/icons-material/EventNoteOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import CodeOutlined from "@mui/icons-material/CodeOutlined";
import NoteAltOutlined from "@mui/icons-material/NoteAltOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import QuestionAnswerOutlined from "@mui/icons-material/QuestionAnswerOutlined";
import { Button, useTheme } from "@mui/material";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import { blue } from "@mui/material/colors";
import Tooltip from "@mui/material/Tooltip";
import size from "lodash/size";
import PropTypes from "prop-types";
import { Fragment, memo, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../components/layout/Flx";
import LoadingFallback from "../../components/LoadingFallback";
import { loanDrilldownSidebarTypes } from "../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useEnv } from "../../hooks/useEnv";
import { usePermissionsHook } from "../../hooks/usePermissionsHook";
import {
  sidebarClear,
  sidebarSetStateValue,
  sidebarTypeSet,
  sidebarTypeToggle,
} from "../../redux/actions/sidebarActions";
import QuickAddHoverMenu, {
  quickAddTooltipProps,
} from "./sidebar/types/addToLoan/QuickAddHoverMenu";
import DrilldownSidebarPane from "./sidebar/DrilldownSidebarPane";
import DrilldownSidebarContentRenderer from "./sidebar/DrilldownSidebarContentRenderer";
import ClosingSidebarButton from "./components/ClosingSidebarButton";
import { useReduxHook } from "../../hooks/useReduxHook";

const LoanDrilldownScreenSidebar = memo(() => {
  const dispatch = useDispatch();
  const sidebarType = useSelector((state) => state.sidebar?.type);

  useEffect(() => {
    // Clear the side drawer state on unmount
    return () => {
      dispatch(sidebarClear());
    };
  }, [dispatch]);

  if (!sidebarType) {
    return (
      <CollapsedContentWrapper>
        <CollapsedContentButtons />
      </CollapsedContentWrapper>
    );
  }

  return (
    <>
      <Suspense
        fallback={
          <DrilldownSidebarPane
            // title="File Preview"
            // onClose={handleHideComments}
            bodyPadding={0}
            initialWidth="580px"
            bodyOverflow="hidden"
          >
            <LoadingFallback
              sx={{
                flexGrow: 1,
                // background: "red",
                background: "#ffffff",
                width: "100%",
                height: "100%",
              }}
            />
          </DrilldownSidebarPane>
        }
      >
        <DrilldownSidebarContentRenderer type={sidebarType} />
      </Suspense>
      <CollapsedContentButtons />
    </>
  );
});

/** Sidebar logic to route between different sidebar types */

const CollapsedContentWrapper = ({ children }) => {
  const styles = useMemo(() => {
    return {
      borderLeft: "1px solid #e0e0e0",
      height: "100%",
      display: "flex",
      background: "#ffffff",
      // Low z-index: when a sidebar/pane is open it covers this rail (panes sit
      // at 13000+). The rail only shows when no pane is open.
      zIndex: 111,
      flexDirection: "column",
      gap: 1,
      flexShrink: 0,
      overflowY: "auto",
      ".MuiSvgIcon-root": { fontSize: 24 },
    };
  }, []);

  return (
    <Box sx={styles}>
      <Flx column>{children}</Flx>
    </Box>
  );
};

const CollapsedContentButtons = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { isUnderwriter } = usePermissionsHook();
  const { isHighLevelUser } = useReduxHook();
  const { isResfin } = useEnv();
  // "Underwriting Notes" preclose, "Diligence Notes" on acquisitions.
  const { notesLabel } = usePipeline();

  const showUWButtons = isUnderwriter || isResfin || isHighLevelUser;
  return (
    <CollapsedContentWrapper>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.CREATE_NEW}
        tooltip="Create a new item on this Loan"
        // label="Loan Comments"
        label="Quick Add"
        hoverMenu={(close) => <QuickAddHoverContent onDone={close} />}
      >
        <AddOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.COMMENTS}
        tooltip="View Comments"
        // label="Loan Comments"
        label="Comments"
      >
        <Badge badgeContent={size(loanDrilldown?.comments)} color="primary">
          <QuestionAnswerOutlined className="thin-8" />
        </Badge>
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.DOCUMENTS}
        tooltip="Quick-view loan documents"
        label="Documents"
      >
        <FolderOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      {showUWButtons && (
        <Fragment>
          <CollapsedSiderbarButton
            type={loanDrilldownSidebarTypes.UW_NOTES}
            tooltip={`View ${notesLabel}`}
            label={notesLabel}
          >
            <NoteAltOutlined className="thin-8" />
          </CollapsedSiderbarButton>
          <CollapsedSiderbarButton
            type={loanDrilldownSidebarTypes.PROCESSING_NOTES}
            tooltip="View Processing Notes"
            label="Processing Notes"
          >
            <FactCheckOutlined className="thin-8" />
          </CollapsedSiderbarButton>
          {!isResfin && (
            <CollapsedSiderbarButton
              type={loanDrilldownSidebarTypes.UW_CHECKLIST}
              tooltip="View Underwriting Checklist"
              // label="Underwriting Checklist"
              label="UW Checklist"
            >
              <ChecklistOutlined className="thin-8" />
            </CollapsedSiderbarButton>
          )}
        </Fragment>
      )}
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.UW_STIPS_AND_CONDITIONS}
        tooltip="View Underwriting Stips & Conditions"
        // label="Underwriting Checklist"
        label="Conditions"
      >
        <GradingOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      {/* The closing cockpit — the same panel the closing calendar opens, on
          the loan. Gates itself to admins / closers; renders nothing for
          everyone else. */}
      <ClosingSidebarButton
        renderTrigger={({ onClick, open, label }) => (
          <CollapsedSiderbarButton
            onClick={onClick}
            active={open}
            tooltip={`View ${label} readiness`}
            label={label}
          >
            <EventAvailableOutlined className="thin-8" />
          </CollapsedSiderbarButton>
        )}
      />
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.KEY_DATES_AND_TOTALS}
        tooltip="View Totals, Calculated Fields & Key Dates"
        label="Totals & Calculations"
      >
        <EventNoteOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.LOAN_CHANGE_HISTORY}
        tooltip="View Loan Activity"
        label="Activity"
      >
        <HistoryOutlined className="thin-8" />
      </CollapsedSiderbarButton>
      <CollapsedSiderbarButton
        type={loanDrilldownSidebarTypes.FORMSPY}
        tooltip="View Loan Data"
        label="JSON"
      >
        <CodeOutlined className="thin-8" />
      </CollapsedSiderbarButton>
    </CollapsedContentWrapper>
  );
};

/** The Quick Add hover menu, with "All items…" opening the full panel. */
const QuickAddHoverContent = ({ onDone }) => {
  const dispatch = useDispatch();
  return (
    <QuickAddHoverMenu
      onDone={onDone}
      onOpenAll={() => {
        dispatch(sidebarSetStateValue(null));
        dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.CREATE_NEW));
      }}
    />
  );
};

QuickAddHoverContent.propTypes = { onDone: PropTypes.func };

/**
 * `hoverMenu(close)` — optional: instead of the plain tooltip, hovering the
 * button opens an interactive menu to its left (the Quick Add shortcuts). It
 * stays open while the pointer is over the menu, and is suppressed while this
 * button's own panel is already open.
 */
const CollapsedSiderbarButton = ({
  type,
  children,
  tooltip,
  label,
  hoverMenu,
  onClick,
  active,
}) => {
  const isTypeActive = useSelector((state) => state.sidebar?.type === type);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  // `onClick` + `active` are for a rail entry that opens its OWN panel instead
  // of a `sidebar.type` pane (the closing sidebar) — same chrome, its own state.
  const isActive = onClick ? !!active : isTypeActive;

  const handleOnClick = () => {
    setMenuOpen(false);
    if (onClick) {
      onClick();
      return;
    }
    dispatch(sidebarTypeToggle(type));
  };

  const tooltipProps = hoverMenu
    ? {
        // Only built while open, so the menu's hook isn't running on every
        // drilldown render for a menu nobody is looking at.
        title: menuOpen ? hoverMenu(() => setMenuOpen(false)) : "",
        open: menuOpen && !isActive,
        onOpen: () => setMenuOpen(true),
        onClose: () => setMenuOpen(false),
        ...quickAddTooltipProps,
      }
    : { title: tooltip, disableInteractive: true };

  return (
    <Tooltip placement={"left"} {...tooltipProps}>
      <Button
        startIcon={children}
        onClick={handleOnClick}
        size="large"
        // color={isActive ? "primary" : "initial"}
        variant="text"
        color={isActive ? "primary" : "inherit"}
        // color={isActive ? "primary" : "inherit"}
        sx={{
          borderRadius: "0px",
          width: "72px",
          px: 0.2,
          py: 2.5,
          fontSize: "10px",
          minWidth: "30px",
          // color: "inherit",
          // color: grey[800],
          lineHeight: 1.3,
          flexDirection: "column",
          // fontWeight: 400,
          fontWeight: isActive ? 600 : 400,
          background: isActive ? blue[50] : "transparent",
          // height: "72px",
          borderLeft: isActive
            ? `3px solid ${theme.palette.primary.main}`
            : "none",

          ".MuiButton-icon": {
            m: 0,
            // fill: isActive ? blue[50] : "inherit",
          },
          "&:hover": {
            background: blue[50],
          },
        }}
      >
        {label}
      </Button>
    </Tooltip>
  );
};

CollapsedContentWrapper.propTypes = {
  children: PropTypes.node,
};

CollapsedSiderbarButton.propTypes = {
  type: PropTypes.string,
  children: PropTypes.node,
  tooltip: PropTypes.string,
  label: PropTypes.node,
  hoverMenu: PropTypes.func,
  onClick: PropTypes.func,
  active: PropTypes.bool,
};

export default LoanDrilldownScreenSidebar;
